import  moment from 'moment';
import { BehaviorSubject, Subject ,  empty as observableEmpty } from 'rxjs';
import { cloneDeep as _cloneDeep, forEach as _forEach } from 'lodash';
import { Component, OnDestroy, OnInit, NgZone } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntil, catchError } from 'rxjs/operators';

import { BlockingIndicatorService } from '../../services/blocking-indicator/blocking-indicator.service';
import { CommonValues } from '../../classes/common-values';
import { CountriesService } from '../../services/countries/countries.service';
import { PaymentConfigService } from '../../services/payment/payment-config.service';
import { DataService } from '../../services/data/data.service';
import { ManageFields } from '../../classes/manage-fields';
import { PostService } from '../../services/post/post.service';
import { SecurityService } from '../../services/security/security.service';
import { SelectOptgroup } from '../../interfaces/select-optgroup.interface';
import { SelectOption } from '../../interfaces/select-option.interface';
import { States } from '../../interfaces/states.interface';
import { StatesService } from '../../services/states/states.service';
import { TimeoutService } from '../../services/timeout/timeout.service';
import {CloverService} from "../../services/payment/clover.service";
import {ValidateCreditCardNumber} from "../../validators/credit-card-number.validator";

declare function paymentFirstData(orderId, token, prodOrder): any;
declare var Clover: any;
const PRINTABLE_CHARS_REGEX = /^[\x20-\x7E]*$/;

@Component({
    selector: 'nsc-order-payment',
    templateUrl: './order-payment.component.html',
    styleUrls: ['./order-payment.component.scss'],
    standalone: false
})
export class OrderPaymentComponent implements OnDestroy, OnInit {
  formGroup: FormGroup;

  unsubscribe$ = new Subject();

  content = {
    totalFees: this.dataService.get().response.orderHeader.totalFee,

    hintZip: this.commonValues.address.hintZipDomestic,
    hintZipDomestic: this.commonValues.address.hintZipDomestic,
    hintZipInternational: this.commonValues.address.hintZipInternational,

    labelState: this.commonValues.address.labelStateUS,
    labelStateUS: this.commonValues.address.labelStateUS,
    labelStateCA: this.commonValues.address.labelStateCA,
    labelStateInternational: this.commonValues.address.labelStateInternational,

    nonceDetails: null,
    errors: [],
    errorTitle: null,
    errorBody: null
  };

  masks = {
    securityCode: [/\d/, /\d/, /\d/, /\d/]
  };

  values = {
    countries$: new BehaviorSubject(<SelectOption[]>[]),
    states$: new BehaviorSubject(<SelectOptgroup[]>[]),
    statesUS: <SelectOptgroup[]>[], // populated by the states API;
    statesCA: <SelectOptgroup[]>[], // populated by the states API;
    stateInternational: null, // to be defined with a value from the state API;

    validationZipCA: this.commonValues.address.validationZipCA,
    validationZipUS: this.commonValues.address.validationZipUS
  };
  paymentErrorValues = {
    creditCard: {
      title: 'Payment Error',
      content: "We’re unable to process your credit card as entered. Please verify that all card details—including the zip/postal code—are correct. If the issue persists, this order will be canceled and you’ll need to start a new order."
    },
    missingDataFields: {
      title: 'Missing Information',
      content:
        'All required fields have not been populated correctly. Please check all fields are entered correctly and resubmit.'
    }
  };
  cloverInstance: any;
  cloverElements: any;
  publicKey: string = '';
  sdkUrl: string = '';
  appCode: string = 'ts';
  public cardNumber: any;
  public cardDate: any;
  public cardCvv: any;
  public cardName: any;
  public cardPostalCode: any
  isError: boolean = false;
  allFieldsValid: boolean = false;
  errorMsg: string = '';
  filedErrors: any = {
    cardName: true,
    cardNumber: true,
    cardDate: true,
    cardCvv: true,
    cardPostalCode: true
  };
  ccFormIsInvalid: boolean = false;
  expDateValidationFailed = false;
  calledTokenize: boolean = false;
  cloverResponse: any;
  invalidCCError = false;
  cardHolderName = "";
  cardHolderNameForm: FormGroup;
  cloverMountSuccessful: boolean = false;

  constructor(
    private blockingIndicatorService: BlockingIndicatorService,
    private commonValues: CommonValues,
    private countriesService: CountriesService,
    private dataService: DataService,
    private formBuilder: FormBuilder,
    private manageFields: ManageFields,
    private postService: PostService,
    private router: Router,
    private securityService: SecurityService,
    private statesService: StatesService,
    private timeoutService: TimeoutService,
    private ngZone: NgZone,
    private cloverService: CloverService
  ) {}

  ngOnInit() {
    this.blockingIndicatorService.open();
    this.cloverService.getEnv();
    const data = this.dataService.get();
    this.cardHolderNameForm = this.formBuilder.group({
      cardHolderName: ['', [Validators.required]],
    })
    this.loadCloverUrlAndKey();
    this.initServiceValues();
    this.initForm();
    this.initIsDomestic();
    this.initUseContactInfo();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initServiceValues(): void {
    // get Ajax values for the SELECT fields;
    this.countriesService.data$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((json: SelectOption[]) => this.values.countries$.next(json));

    // break the states up by their country;
    this.statesService.data$.pipe(takeUntil(this.unsubscribe$)).subscribe((json: States) => {
      this.values.statesUS = json.us;
      this.values.statesCA = json.canada;
      this.values.stateInternational = json.international;
      // set default value;
      this.values.states$.next(this.values.statesUS);
    });
  }

  initForm(): void {
    // initialize the form with the default required fields;
    // required fields will change based on selected options and parameters;
    this.formGroup = this.formBuilder.group(
      {
        useContactInfo: new FormControl(),
        addressLine1: new FormControl(null, [Validators.required]),
        addressLine2: new FormControl(),
        city: new FormControl(null, [Validators.required, Validators.minLength(2)]),
        state: new FormControl(null, [Validators.required]),
        zip: new FormControl(null, this.values.validationZipUS),
        country: new FormControl(this.commonValues.api.us, [Validators.required])
      }
    );

    // any change to the form means the user is actively using the app, so extend the system timeout;
    this.formGroup.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
      this.timeoutService.startWarningCountdown();
      this.refreshAllFieldsValidBool();
    });
  }

  initIsDomestic(): void {
    const controls = this.formGroup.controls;

    // watch the `country` field because when toggled we want to change content on other fields;
    this.formGroup.controls.country.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value: string) => {
        const isUS = value === this.commonValues.api.us;
        const isCA = value === this.commonValues.api.ca;
        const isDomestic = isUS || isCA;

        // configure zip;
        // us & canda have different validations;
        if (isDomestic) {
          controls.zip.setValidators(
            isUS ? this.values.validationZipUS : this.values.validationZipCA
          );
          this.content.hintZip = this.content.hintZipDomestic;
        }
        // international has no zip validation;
        else {
          controls.zip.clearValidators();
          this.content.hintZip = this.content.hintZipInternational;
        }

        // validation rules may have changed, but we dont want to show a validation error;
        // remove any previously entered value, and reset the field;
        this.manageFields.enable([controls.zip]);

        // configure states (labels and values) based on country;
        if (isDomestic) {
          if (isUS) {
            this.content.labelState = this.content.labelStateUS;
            this.values.states$.next(this.values.statesUS);
          } else {
            this.content.labelState = this.content.labelStateCA;
            this.values.states$.next(this.values.statesCA);
          }

          this.manageFields.enable([controls.state]);
        }
        // or disable the state field if international;
        else {
          this.content.labelState = this.content.labelStateInternational;

          this.manageFields.disable([controls.state]);
        }
      });
  }

  initUseContactInfo(): void {
    const controls = this.formGroup.controls;

    // watch the `useContactInfo` field because when toggled we want to fill/empty form fields;
    controls.useContactInfo.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value: string) => {
        const requestor = this.dataService.get().response.student;
        let newValues = {
          addressLine1: null,
          addressLine2: null,
          zip: null,
          city: null,
          state: null,
          country: this.commonValues.api.us
        };

        // if the user says to use their contact Info, pull it from the Data service;
        if (value === this.commonValues.api.yes) {
          newValues = {
            addressLine1: requestor.street1,
            addressLine2: requestor.street2,
            zip: requestor.zip === this.commonValues.defaultZip ? null : requestor.zip,
            city: requestor.city,
            state: requestor.state,
            country: requestor.country
          };
        }

        this.manageFields.reset([
          controls.addressLine1,
          controls.zip,
          controls.city,
          controls.state
        ]);

        this.formGroup.patchValue(newValues);
        this.refreshAllFieldsValidBool();
      });
  }

  cancel(): void {
    this.securityService.cancelOrder();
  }
  scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  refreshAllFieldsValidBool(){
    this.allFieldsValid = this.formGroup.valid && this.validateCloverCardForm();
  }

  validateCloverCardForm(): boolean {
    return !Object.values(this.filedErrors).some(error => error === true);
  }

  submitPayment() {
    if(!this.cloverResponse){
      this.setPaymentCardErrorMessage(false);
    }
    else{
      this.continue();
    }
  }

  /** WCAG 2.4.3: move focus to first element with aria-invalid="true",
   *  or fall back to the error summary banner. */
  private focusFirstError(): void {
    const firstInvalidField = document.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (firstInvalidField) {
      firstInvalidField.focus();
    } else {
      const errorSummary = document.getElementById('payment-error-summary');
      if (errorSummary) {
        errorSummary.focus();
      }
    }
  }

  setPaymentCardErrorMessage(isInvalidForm) {
    this.blockingIndicatorService.close();
    // checks if all the fields has been fillout however there was some error while processing the payment
    if (this.formGroup.valid && !isInvalidForm) {
      this.content.errorTitle = this.paymentErrorValues.creditCard.title;
      this.content.errorBody = this.paymentErrorValues.creditCard.content;
    } // check if the form is missing any field to be fillout
    else if (isInvalidForm) {
      this.content.errorTitle = this.paymentErrorValues.missingDataFields.title;
      this.content.errorBody = this.paymentErrorValues.missingDataFields.content;
    }

    this.content.errors = [{}];
  }

  private mapCloverResponseToPaymentData(data: any): void {
    const { token, card } = this.cloverResponse;

    data.cloverToken = token;
    data.expirationYear = card.exp_year;
    data.expirationMonth = card.exp_month;
    data.ccType = card.brand;
    data.name = this.cardHolderName;
    data.zip = card.address_zip;
  }

  continue(): void {
    this.blockingIndicatorService.open();
    const controls = this.formGroup.controls;
    const production = this.dataService.get().response.orderHeader.chFlag === 'P';

    this.commonValues.payeezyCount = this.commonValues.payeezyCount + 1;

    // create a clone of the form data so we can append payeezy data before submitting to the data service;
    let data = _cloneDeep(this.formGroup.value);

    this.mapCloverResponseToPaymentData(data);

    // this form requires the values in a different format;
    // need to convert the value property into the value of the name property;
    _forEach(this.values.countries$.value, country => {
      if (country.value === controls.country.value) {
        data.country = country.name;
      }
    });
    _forEach(this.values.states$.value, region => {
      _forEach(region, group => {
        _forEach(group, state => {
          if (state.value === controls.state.value) {
            data.state = state.name;
          }
        });
      });
    });

    this.dataService.resetOrderPayment();

    this.dataService.save(data, 'form.payment');

    // post the data to the back-end;
    // once that's complete move onto the order-confirmation route;
    this.postService
      .payment()
      .pipe(
        takeUntil(this.unsubscribe$),
        catchError(error => {
          if (this.commonValues.payeezyCount < this.commonValues.payeezyCountLimit) {
            // show the errors and prevent the user from moving forward;
            this.setPaymentCardErrorMessage(false);
            this.content.errors = [error.message];
            // hide the loading indicator so the user can edit the form;
            this.blockingIndicatorService.close();
            this.scrollToTop();
            setTimeout(() => this.focusFirstError(), 100);
          }
          // the error threshold has been hit, so redirect the user to the message page saying the order is done;
          else {
            this.securityService.setDeactivateWarning(false);

            this.router.navigate(
              [this.commonValues.routes.messagePayment],
              this.commonValues.routes.extras
            );
          }
          return observableEmpty();
        })
      )
      .subscribe(() => {
        const continueToOrderConfirmation = () => {
          window.setTimeout(() => {
            this.securityService.setDeactivateWarning(false);

            this.router.navigate(
              [this.commonValues.routes.orderConfirmation],
              this.commonValues.routes.extras
            );
          }, this.commonValues.loading.delay);
        };

        // if there is a signature to post, do it, otherwise move on;
        if (this.dataService.get().form.consent.signature) {
          this.postService
            .consent()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
              () => {
                continueToOrderConfirmation();
              },
              error => {
                // ignore consent error and send to the next screen
                console.log(error);
                continueToOrderConfirmation();
              }
            );
        } else {
          continueToOrderConfirmation();
        }
      });
    //  }
    //  });
  }

  loadCloverUrlAndKey(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.cloverService.getMethodData("/api/tsorder/system-config/merchant-key").subscribe(
        (data: any) => {
          console.log('Payment service : ' + JSON.stringify(data));

          if (!data?.merchantPublicKey) {
            this.handleCloverLoadError("System is unavailable, please try again at a later time.");
            resolve();
            return;
          }

          this.publicKey = data.merchantPublicKey;
          this.loadCloverSDKUrl(resolve, reject);
        },
        error => {
          this.handleCloverLoadError("System is unavailable, please try again at a later time.");
          this.securityService.setDeactivateWarning(false);

          this.router.navigate(
            [this.commonValues.routes.messageError],
            this.commonValues.routes.extras
          );

          resolve(); // Resolve instead of reject since the error is handled
        }
      );
    });
  }

  private loadCloverSDKUrl(resolve: () => void, reject: (error: any) => void): void {
    this.cloverService.getCloverSDKUrlAndPublicKey().subscribe(
      (res: any) => {
        this.sdkUrl = res.data?.cloverSdkUrl;
        this.initializeClover();

        this.blockingIndicatorService.close();
        resolve();
      },
      error => {
        this.handleCloverLoadError("System is unavailable, please try again at a later time.");
        reject(error);
      }
    );
  }

  private handleCloverLoadError(message: string): void {
    this.isError = true;
    this.errorMsg = message;
  }

  loadCloverSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof Clover !== 'undefined') {
        console.log('Clover SDK already loaded');
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = this.sdkUrl;
      script.async = true;

      script.onload = () => {
        console.log('Clover SDK loaded successfully!');
        resolve();
      };
      script.onerror = (error) => {
        console.error('Error loading Clover SDK:', error);
        this.isError = true;
        this.errorMsg = "System is unavailable, please try again at a later time.";
        reject(error);
      };

      document.body.appendChild(script);
    });
  }


  initializeClover() {
    this.loadCloverSDK().then(() => {
      const styles = {
        "html": {
          display: "flex",
          "align-items": "center"
        }
      }
      if (typeof Clover !== 'undefined') {
        this.cloverInstance = new Clover(this.publicKey);
        this.cloverElements = this.cloverInstance.elements();
        this.cardNumber = this.cloverElements.create('CARD_NUMBER', styles);
        this.cardDate = this.cloverElements.create('CARD_DATE', styles);
        this.cardCvv = this.cloverElements.create('CARD_CVV',styles);
        this.cardPostalCode = this.cloverElements.create('CARD_POSTAL_CODE',styles);
        this.cardNumber.mount('#card-number');
        this.cardDate.mount('#card-date');
        this.cardCvv.mount('#card-cvv');
        this.cardPostalCode.mount('#card-postal-code');
        this.cloverElements.create('CARD');
        this.addCloverEventListeners();
        console.log("Success");
        this.cloverMountSuccessful = true;
      } else {
        console.error('Clover SDK is still undefined!');
        this.isError = true;
        this.errorMsg = "System is unavailable, please try again at a later time.";
      }
    }).catch(error => {
      console.error('Failed to load Clover SDK:', error);
      this.isError = true;
      this.errorMsg = "System is unavailable, please try again at a later time.";
    });
  }

  addCloverEventListeners() {
    this.cardName = document.getElementById("card-name");
    this.cardName.addEventListener('change', (event: any) => this.handleEvent(event.CARD_NAME, 'card-name-errors', 'card-name', 'change'));
    this.cardName.addEventListener('blur', (event: any) => this.handleEvent(event.CARD_NAME, 'card-name-errors', 'card-name', 'blur'));

    this.cardNumber.addEventListener('change', (event: any) => this.handleEvent(event.CARD_NUMBER, 'card-number-errors', 'card-number', 'change'));
    this.cardNumber.addEventListener('blur', (event: any) => this.handleEvent(event.CARD_NUMBER, 'card-number-errors', 'card-number', 'blur'));

    this.cardDate.addEventListener('change', (event: any) => this.handleEvent(event.CARD_DATE, 'card-date-errors', 'card-date', 'change'));
    this.cardDate.addEventListener('blur', (event: any) => this.handleEvent(event.CARD_DATE, 'card-date-errors', 'card-date', 'blur'));

    this.cardCvv.addEventListener('change', (event: any) => this.handleEvent(event.CARD_CVV, 'card-cvv-errors', 'card-cvv', 'change'));
    this.cardCvv.addEventListener('blur', (event: any) => this.handleEvent(event.CARD_CVV, 'card-cvv-errors', 'card-cvv', 'blur'));

    this.cardPostalCode.addEventListener('blur', (event: any) => this.handleEvent(event.CARD_POSTAL_CODE, 'card-postal-code-errors', 'card-postal-code', 'blur'));
  }

  handleEvent(event: any, errorElementId: string, inputboxId: string, eventType: string) {
    this.isError = false;
    const errorElement = document.getElementById(errorElementId);
    const fieldKey = this.kebabToCamel(inputboxId);
    if (fieldKey == 'cardName') { // handling card holder name separately from iframe
      this.validateCreditCardHolderName(errorElement, fieldKey);
    } else if (errorElement) {
      this.validateCloverCCFields(event, errorElement, fieldKey, eventType);
    }
    this.refreshAllFieldsValidBool();
    this.handleCloverTokenizationCall(fieldKey, eventType);
  }

  validateCloverCCFields(event: any, errorElement: any, fieldKey: string, eventType: string) {
    if (fieldKey === 'cardDate' && this.expDateValidationFailed && eventType === 'blur') {
      return;
    }

    const fieldElementId = fieldKey.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
    const fieldElement = document.getElementById(fieldElementId);

    if (event.error) {
      this.filedErrors[fieldKey] = true;
      // Clear first so aria-live regions always detect a DOM mutation and re-announce
      errorElement.textContent = '';
      setTimeout(() => {
        errorElement.textContent = "Error: " + event.error;
      }, 0);
      if (fieldElement) {
        fieldElement.setAttribute('aria-invalid', 'true');
      }
    } else {
      this.filedErrors[fieldKey] = false;
      errorElement.textContent = '';
      if (fieldElement) {
        fieldElement.removeAttribute('aria-invalid');
      }
    }
  }

  handleCloverTokenizationCall(fieldKey: string, eventType: string) {
    this.ccFormIsInvalid = Object.values(this.filedErrors).some(error => error === true);
    if (this.ccFormIsInvalid) {
      return;
    } else if (!this.ccFormIsInvalid && !this.calledTokenize && (eventType == "change" || (eventType == "blur" && fieldKey == "cardPostalCode"))) {
      console.log('form is valid');
      this.getCloverToken();
    }
  }

  /*Once the card number is entered clover stops validating the expired date, therefore custom check once the token is generated
    * because we do not have access to the form values until the token is generated. */
  validateCardExpDate(card: any) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    const expYear = parseInt(card.exp_year);
    const expMonth = parseInt(card.exp_month);
    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
      const errorElement = document.getElementById('card-date-errors');
      const dateElement = document.getElementById('card-date');
      if (errorElement) {
        errorElement.textContent = "Error: Card expiry is invalid";
      }
      if (dateElement) {
        dateElement.setAttribute('aria-invalid', 'true');
      }
      this.filedErrors['cardDate'] = true;
      this.ccFormIsInvalid = true;
      this.calledTokenize = false;
      this.expDateValidationFailed = true;
      return;
    } else {
      const dateElement = document.getElementById('card-date');
      if (dateElement) {
        dateElement.setAttribute('aria-invalid', 'false');
      }
      this.expDateValidationFailed = false;
    }
  }

  getCloverToken() {
    console.log('calling tokenize');
    this.calledTokenize = true;
    this.cloverInstance.createToken().then((response: any) => {
      if (response.token) {
        this.validateCardExpDate(response.card)
        this.cloverResponse = response;
        this.calledTokenize = false;
      } else {
        console.error("Error:", response);
        this.invalidCCError = true;
        this.isError = true;
        this.calledTokenize = false;
        this.errorMsg = "Invalid credit card information provided.";
      }
    }).catch((error: any) => {
      this.invalidCCError = true;
      console.error("Clover Error:", error);
      this.isError = true;
      this.calledTokenize = false;
      this.errorMsg = "Invalid credit card information provided.";
    });
  }

  validateCreditCardHolderName(errorElement: any, fieldKey: string) {
    const cardNameElement = document.getElementById('card-name');
    const trimmedName = this.cardHolderName.trim();
    if (trimmedName == '') {
      errorElement.textContent = "Error: Card Holder Name is required";
      this.filedErrors[fieldKey] = true;
    } else if (!PRINTABLE_CHARS_REGEX.test(trimmedName)) {
      errorElement.textContent = "Error: Invalid name";
      this.filedErrors[fieldKey] = true;
    } else if (trimmedName.length > 100) {
      errorElement.textContent = "Error: Name must be 100 characters or less";
      this.filedErrors[fieldKey] = true;
    } else {
      this.filedErrors[fieldKey] = false;
      errorElement.textContent = '';
    }

    if (cardNameElement) {
      cardNameElement.setAttribute('aria-invalid', this.filedErrors[fieldKey]);
    }
  }

  kebabToCamel(kebab: string) {
    return kebab.replace(/-([a-z])/g, (g) => g[1].toLocaleUpperCase());
  }
}
